<template>
  <div class="mt-6">
    <div v-if="error" class="pt-6 flex flex-col text-center">
      <i class="fas fa-exclamation-triangle mb-3"></i> {{ error }}
    </div>
    <div v-else>
      <panel class="pt-6 mb-6">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/4 px-2">
            <label for="search_term" class="text-sm">Search</label>
            <input type="text" id="search_term" v-model="searchParams.q" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
          </div>

          <div class="w-full md:w-1/4 px-2">
            <label for="status_id" class="text-sm">Listing Status</label>
            <select id="status_id" v-model="searchParams.status_id" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
              <option v-for="status in statusOptions" :key="status.id" :value="status.id">
                {{ status.name }}
              </option>
            </select>
          </div>

          <div class="w-full md:w-1/4 px-2">
            <label for="last_activity_after" class="text-sm">Last Activity After:</label>
            <date-time-picker id="last_activity_after" v-model="searchParams.last_activity_after" clear-button :max-date="searchParams.last_activity_before" format="d LLLL yyyy" value-format="yyyy-LL-dd" value-type="String" auto-close constraints-format="yyyy-LL-dd" :class="'block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm mt-2'" :time-picker="false" />
          </div>

          <div class="w-full md:w-1/4 px-2">
            <label for="last_activity_before" class="text-sm">Last Activity Before:</label>
            <date-time-picker id="last_activity_before" v-model="searchParams.last_activity_before" clear-button :min-date="searchParams.last_activity_after" format="d LLLL yyyy" value-format="yyyy-LL-dd" value-type="String" auto-close constraints-format="yyyy-LL-dd" :class="'block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm mt-2'" :time-picker="false" />
          </div>
        </div>
      </panel>

      <div v-if="listings && fees" class="flex justify-end mb-6">
        <button class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow" @click="discloseNonListedCompletion">
          Disclose Non-Marketed Property
        </button>
      </div>

      <loader v-if="listings === null || fees === null" />
      <div v-else-if="listings.data.length === 0" class="pt-6 flex flex-col text-center">
        <i class="fas fa-check-circle mb-3"></i> You're up to date. No actions required.
      </div>
      <panel v-else class="pt-6 mb-12">
        <div class="w-full max-w-full overflow-x-auto overflow-y-hidden">
          <table class="table-auto text-center w-full">
            <thead class="uppercase text-xs">
              <tr>
                <th class="text-left p-2 whitespace-no-wrap" colspan="2">Property Listing</th>
                <th class="text-center p-2 whitespace-no-wrap">Asking Price</th>
                <th class="text-center p-2 whitespace-no-wrap">Listing Status</th>
                <th class="text-center p-2 whitespace-no-wrap">Updated</th>
                <th class="text-right p-2">Update Status</th>
              </tr>
            </thead>
            <tbody class="text-sm">
              <tr v-for="listing in listings.data" :key="listing.id" class="border-t border-grey-lighter">
                <td class="text-left p-2 font-bold w-24">
                  <img class="icon-img w-24 h-16 my-2 rounded object-center object-cover"
                    :src="listing.main_image.large_url" v-if="listing.main_image" />
                  <div v-else class="relative w-24 h-16 px-6 pt-2 my-2 rounded bg-tertiary">
                    <icon name="property_cms" class="text-white"/>
                  </div>
                </td>
                <td class="p-2 text-left">
                  <strong><a :href="`/property-management/${listing.id}`" class="no-underline hover:underline text-black" target="_blank">
                    {{ [listing.address_name_number, listing.address_line2, listing.address_line3, listing.address_town, listing.address_line4, listing.address_postcode].filter(Boolean).join(', ') }}
                  </a></strong>
                  <small class="block text-grey-dark mt-1">
                    ID: {{ listing.feed_property_id || listing.id }}
                  </small>
                </td>
                <td class="p-2 text-center">
                  <span v-if="listing.price_qualifier_id === 7">POA</span>
                  <span v-else>{{ listing.price | gbp }}</span>
                </td>
                <td class="p-2 text-center" v-if="listing.deleted_at">
                  <strong class="block">Taken Off-Market as {{ listingStatus(listing.status_id) }}</strong>
                  <span class="block text-grey-dark mt-1">
                    {{ listing.deleted_at | datetime }}
                  </span>
                </td>
                <td v-else-if="!listing.published">
                  <strong class="block">Unpublished as {{ listingStatus(listing.status_id) }}</strong>
                </td>
                <td v-else>
                  <strong class="block">{{ listingStatus(listing.status_id) }}</strong>
                </td>
                <td class="p-2 text-center">
                  <strong>{{ listing.updated_at | date }}</strong>
                  <small class="text-grey-dark block">{{ listing.updated_at | time }}</small>
                </td>
                <td class="p-2 text-right whitespace-no-wrap">
                  <button class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-3 text-sm border border-tertiary hover:border-transparent rounded shadow whitespace-no-wrap" @click="editListing = listing">
                    <i class="fas fa-pencil-alt"></i>
                    <span>
                      Update
                    </span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <paginator :data="listings" @change="pageChanged"/>
      </panel>
    </div>

    <modal v-if="completionForm" @leave="closeModal" show-close-btn width="640px" modal-class="bg-white rounded-lg shadow-lg p-6 relative my-6">
      <h2 class="font-bold text-sm mb-2">Set Outcome</h2>
      <p class="mb-6" v-if="editListing">{{ [editListing.address_name_number, editListing.address_line2, editListing.address_line3, editListing.address_town, editListing.address_line4, editListing.address_postcode].filter(Boolean).join(', ') }}</p>
      <div v-else class="mb-6">
        <p class="mb-3">{{ completionForm.address }}</p>
        <button class="text-black py-2 px-6 text-sm bg-whit hover:bg-grey-lighter rounded shadow border" @click="addressLookupModalOpen = true">
          Select Address
        </button>
        <address-lookup-modal
          v-if="addressLookupModalOpen"
          @leave="addressLookupModalOpen = false"
          v-model="selectedAddress" />

          <div v-if="[7, 8].includes(selectedOffice.network_id)" class="flex bg-grey-lighter p-3 my-6 text-sm leading-normal">
            <i class="fas fa-info-circle mr-3"></i>
            <p>A non-marketed property is an instruction where the initial enquiry was received within the Licensees Fine & Country Territory but was not marketed on the Fine & Country website and was instead marketed in other ways, such as Invisible Homes.</p>
          </div>
      </div>

      <div v-if="isConfirming">
        <div class="bg-yellow-lighter rounded p-3 flex mb-6">
          <i class="fas fa-exclamation-triangle text-yellow-dark mr-3"></i>
          <p class="text-sm leading-normal">
            <strong class="block">Please review your submission below.</strong>
            Your submission is final. You will not be able to update your submission once it's confirmed.
          </p>
        </div>

        <p class="text-sm leading-normal mb-3">
          <strong class="block">Outcome:</strong>
          {{ completionStatus(completionForm.status_id) }}
        </p>

        <div v-if="[1, 2].includes(completionForm.status_id)" class="mb-3">
          <p class="text-sm leading-normal mb-3">
            <strong class="block">Offer Accepted Date:</strong>
            {{ completionForm.offer_accepted_at | date }}
          </p>

          <p class="text-sm leading-normal mb-3">
            <strong class="block">Accepted Offer Price:</strong>
            {{ completionForm.final_offer_price | gbp }}
          </p>

          <p class="text-sm leading-normal mb-3">
            <strong class="block">Exchange Date:</strong>
            {{ completionForm.exchanged_at | date }}
          </p>

          <p class="text-sm leading-normal mb-3">
            <strong class="block">Completion Date:</strong>
            {{ completionForm.completed_at | date }}
          </p>

          <p class="text-sm leading-normal mb-3">
            <strong class="block">Final Sale Price:</strong>
            {{ completionForm.completion_price | gbp }}
          </p>

          <div v-if="fees.network.fee_basis_id === 2">
            <p class="text-sm leading-normal mb-3">
              <strong class="block">Gross Commission (Inclusive of VAT):</strong>
              {{ completionForm.gross_commission | gbp }}
              <span v-if="grossCommissionType === 'percent'">
                ({{ completionForm.gross_commission_percent }}% of sale price)
              </span>
            </p>

            <p class="text-sm leading-normal mb-3" v-if="completionForm.has_third_party_commission">
              <strong class="block">{{ completionForm.third_party_name || 'Third Party' }} Commission (Inclusive of VAT):</strong>
              {{ commissionThirdParty | gbp }}
              <span v-if="netCommissionType === 'percent'">
                ({{ 100 - completionForm.net_commission_percent }}% of gross commission)
              </span>
            </p>

            <p class="text-sm leading-normal mb-3" v-if="completionForm.has_third_party_commission">
              <strong class="block">Net Commission (Inclusive of VAT):</strong>
              {{ completionForm.net_commission | gbp }}
              <span v-if="netCommissionType === 'percent'">
                ({{ completionForm.net_commission_percent }}% of gross commission)
              </span>
            </p>

            <p class="text-sm leading-normal mb-3">
              <strong class="block">{{ selectedOffice.name }} Share:</strong>
              {{ commissionOffice | gbp }} ({{ 100 - fees.office.commission_share }}% of {{ completionForm.has_third_party_commission ? 'net' : 'gross' }} commission)
            </p>

            <p class="text-sm leading-normal mb-3">
              <strong class="block">Head Office Share:</strong>
              {{ commissionNetwork | gbp }} ({{ fees.office.commission_share }}% of {{ completionForm.has_third_party_commission ? 'net' : 'gross' }} commission)
            </p>

            <div v-if="[7, 8].includes(selectedOffice.network_id)" class="flex bg-grey-lighter p-3 my-6 text-sm leading-normal">
              <i class="fas fa-info-circle mr-3"></i>
              <p>Fine & Country Head Office reserve the right to ask you to provide us with proof of your commission fee.</p>
            </div>
          </div>
        </div>

        <loader v-if="saving" />
        <div class="flex" v-else>
          <button class="text-white py-2 px-6 text-sm bg-tertiary rounded shadow mr-3" @click="save">
            Confirm
          </button>
          <button class="text-black py-2 px-6 text-sm bg-white rounded shadow border" @click="isConfirming = false">
            Back
          </button>
        </div>
      </div>

      <div v-else>
        <div class="mb-3" v-if="editListing">
          <input type="radio" id="status_id_3" :value="3" @change="resetDates" v-model="completionForm.status_id">
          <label for="status_id_3">Duplicate Property Listing</label>
        </div>

        <div class="mb-3" v-if="editListing">
          <input type="radio" id="status_id_0" :value="0" @change="resetDates" v-model="completionForm.status_id">
          <label for="status_id_0">Withdrawn</label>
        </div>

        <div class="mb-3" v-if="editListing || completionForm.address">
          <input type="radio" id="status_id_1" :value="1" @change="resetDates" v-model="completionForm.status_id">
          <label for="status_id_1">Exchanged</label>
        </div>

        <div class="mb-6" v-if="editListing || completionForm.address">
          <input type="radio" id="status_id_2" :value="2" @change="resetDates" v-model="completionForm.status_id">
          <label for="status_id_2">Completed</label>
          <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('status_id')" v-text="errorBag.get('status_id')"></small>
        </div>

        <div v-if="[1, 2].includes(completionForm.status_id) && (editListing || (!editListing && completionForm.address))">
          <div class="mb-6">
            <label for="offer_accepted_at" class="text-sm">Offer Accepted Date:</label>
            <date-time-picker id="offer_accepted_at" v-model="completionForm.offer_accepted_at" format="d LLLL yyyy" value-format="yyyy-LL-dd" value-type="String" auto-close constraints-format="yyyy-LL-dd" :class="'block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm mt-2'+(errorBag.get('offer_accepted_at') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')" :max-date="maxOfferAcceptedDate" :time-picker="false" />
            <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('offer_accepted_at')" v-text="errorBag.get('offer_accepted_at')"></small>
          </div>

          <div class="mb-6">
            <label for="final_offer_price" class="text-sm">Accepted Offer Price (£):</label>
            <input type="text" id="final_offer_price" v-model.number="completionForm.final_offer_price" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('final_offer_price') }">
            <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('final_offer_price')" v-text="errorBag.get('final_offer_price')"></small>
          </div>

          <div class="mb-6">
            <label for="exchanged_at" class="text-sm">Exchange Date:</label>
            <date-time-picker id="exchanged_at" v-model="completionForm.exchanged_at" format="d LLLL yyyy" value-format="yyyy-LL-dd" value-type="String" auto-close constraints-format="yyyy-LL-dd" :class="'block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm mt-2'+(errorBag.get('exchanged_at') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')" :min-date="completionForm.offer_accepted_at" :max-date="maxExchangeDate" :time-picker="false" />
            <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('exchanged_at')" v-text="errorBag.get('exchanged_at')"></small>
          </div>

          <div class="mb-6">
            <label for="completed_at" class="text-sm">Completion Date:</label>
            <date-time-picker id="completed_at" v-model="completionForm.completed_at" :disabled="!completionForm.exchanged_at" format="d LLLL yyyy" value-format="yyyy-LL-dd" value-type="String" auto-close constraints-format="yyyy-LL-dd" :class="'block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm mt-2'+(errorBag.get('completed_at') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')" :min-date="completionForm.exchanged_at" :max-date="maxCompletionDate" :time-picker="false" />
            <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('completed_at')" v-text="errorBag.get('completed_at')"></small>
          </div>

          <div class="mb-6">
            <label for="completion_price" class="text-sm">Final Sale Price (£):</label>
            <input type="text" id="completion_price" v-model.number="completionForm.completion_price" @change="recalculateGrossCommission" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('completion_price') }">
            <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('completion_price')" v-text="errorBag.get('completion_price')"></small>
          </div>

          <div class="mb-6" v-if="fees.network.fee_basis_id === 2">
            <label for="gross_commission" class="text-sm">Gross Commission (Inclusive of VAT):</label>
            <div class="flex flex-wrap -mx-2">
              <div class="w-2/5 px-2">
                <input v-if="grossCommissionType === 'fixed'" type="text" id="gross_commission" v-model.number="completionForm.gross_commission" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('gross_commission') }">
                <input v-else type="text" id="gross_commission" v-model.number="completionForm.gross_commission_percent" @change="recalculateGrossCommission" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('gross_commission_percent') }">
              </div>
              <div class="w-3/5 px-2">
                <select v-model="grossCommissionType" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
                  <option :value="null">Please Select</option>
                  <option value="fixed">Fixed Fee (£ inc. VAT)</option>
                  <option value="percent">Percentage (% inc. VAT)</option>
                </select>
              </div>
            </div>
            <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('gross_commission')" v-text="errorBag.get('gross_commission')"></small>
            <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('gross_commission_percent')" v-text="errorBag.get('gross_commission_percent')"></small>
          </div>

          <div class="mb-6" v-if="fees.network.fee_basis_id === 2">
            <label for="has_third_party_commission" class="text-sm">Is there a third party entitled to an amount of your commission?:</label>
            <select id="has_third_party_commission" v-model.number="completionForm.has_third_party_commission" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('has_third_party_commission') }">
              <option :value="true">Yes</option>
              <option :value="false">No</option>
            </select>
            <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('has_third_party_commission')" v-text="errorBag.get('has_third_party_commission')"></small>
          </div>

          <div v-if="completionForm.has_third_party_commission && fees.network.fee_basis_id === 2">
            <div class="mb-6">
              <label for="net_commission" class="text-sm">Net Commission (Inclusive of VAT):</label>
              <div class="flex flex-wrap -mx-2">
                <div class="w-2/5 px-2">
                  <input v-if="netCommissionType === 'fixed'" type="text" id="net_commission" v-model.number="completionForm.net_commission" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('net_commission') }">
                  <input v-else type="text" id="net_commission" v-model.number="completionForm.net_commission_percent" @change="recalculateNetCommission" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('net_commission_percent') }">
                </div>
                <div class="w-3/5 px-2">
                  <select v-model="netCommissionType" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
                    <option :value="null">Please Select</option>
                    <option value="fixed">Fixed Fee from gross commission (£ inc. VAT)</option>
                    <option value="percent">Percentage of gross commission (% inc. VAT)</option>
                  </select>
                </div>
              </div>
              <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('net_commission')" v-text="errorBag.get('net_commission')"></small>
              <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('net_commission_percent')" v-text="errorBag.get('net_commission_percent')"></small>
            </div>

            <div class="mb-6">
              <label for="third_party_name" class="text-sm">Please provide the name of the third party who is entitled to an amount of your commission, and please upload supporting material / documents below:</label>
              <input type="text" id="third_party_name" v-model.number="completionForm.third_party_name" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('third_party_name') }">
              <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('third_party_name')" v-text="errorBag.get('third_party_name')"></small>
            </div>
          </div>

          <div class="mb-6" v-if="fees.network.fee_basis_id === 2">
            <label for="attachments" class="text-sm">Supporting Documents:</label>
            <div class="mt-2">
              <input type="file" name="attachments" accept="image/*,application/pdf" multiple @change="completionForm.attachments = Array.from($event.target.files)" />
            </div>
            <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('attachments')" v-text="errorBag.get('attachments')"></small>
          </div>
        </div>

        <div v-else-if="completionForm.status_id === 3" class="flex bg-grey-lighter p-3 mb-6 text-sm leading-normal">
          <i class="fas fa-info-circle mr-3"></i>
          <p>A duplicate property listing is where a property is duplicated and marked as sold for website purposes (post completion), meaning there are two records of the same property.</p>
        </div>
        <div class="mb-6" v-if="completionForm.status_id === 0">
          <select v-model="completionForm.withdrawn_status_id" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
            <option :value="null">Please Select</option>
            <option :value="1">Sold through other agent</option>
            <option :value="2">Vendor has decided not to sell - permanently removed from Market</option>
            <option :value="3">Instructed another agent</option>
            <option :value="4">Vendor has decided not to sell but will relaunch within 6 months with you</option>
            <option :value="5">Vendor has decided not to sell and will relaunch with another agent</option>
            <option :value="6">Other</option>
          </select>
          <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('withdrawn_status_id')" v-text="errorBag.get('withdrawn_status_id')"></small>
        </div>
        <div class="mb-6" v-if="completionForm.status_id === 0 && completionForm.withdrawn_status_id === 1">
          <label for="selling_agent" class="text-sm">Selling Agent:</label>
            <input type="text" id="selling_agent" v-model.number="completionForm.selling_agent" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('final_offer_price') }">
            <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('selling_agent')" v-text="errorBag.get('selling_agent')"></small>
        </div>
        <div class="mb-6" v-if="completionForm.status_id !== null"></div>
        <div class="mb-6" v-if="completionForm.status_id !== null">
          <label for="notes" class="text-sm">Please note here any other pertinent information here:</label>
          <textarea id="notes" rows="5" v-model.number="completionForm.notes" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('notes') }"></textarea>
          <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('notes')" v-text="errorBag.get('notes')"></small>
        </div>

        <button v-if="completionForm.status_id !== null" :disabled="completionFormDisabled" class="text-white py-2 px-6 text-sm bg-tertiary rounded shadow" @click="isConfirming = true">
          <span :class="completionFormDisabled ? 'opacity-50' : 'opacity-100'">Continue</span>
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import { v2Api } from '@/http'
import ErrorBag from '@/ErrorBag'
import { mapActions, mapState } from 'vuex'
import debounce from 'lodash/debounce'
import moment from 'moment'
import DateTimePicker from 'vue-vanilla-datetime-picker'
import AddressLookupModal from '@/components/AddressLookupModal.vue'

export default {
  components: {
    DateTimePicker,
    AddressLookupModal
  },
  data () {
    return {
      searchParams: {
        q: null,
        last_activity_after: null,
        last_activity_before: null,
        status_id: null,
        page: 1
      },
      pageHasChanged: false,
      listings: null,
      fees: null,
      editListing: null,
      completionForm: null,
      grossCommissionType: null,
      netCommissionType: null,
      addressLookupModalOpen: false,
      selectedAddress: {
        address_name_number: null,
        address_line2: null,
        address_line3: null,
        address_town: null,
        address_line4: null,
        address_postcode: null
      },
      isConfirming: false,
      saving: false,
      error: null,
      errorBag: new ErrorBag(),
      statusOptions: [
        { id: null, name: '[All Statuses]' },
        { id: 1, name: 'Available (Off-Market)' },
        { id: 2, name: 'SSTC' },
        { id: 4, name: 'Under Offer' },
        { id: 7, name: 'Sold' },
        { id: 9, name: 'Exchanged' },
        { id: 10, name: 'Completed' },
        { id: 11, name: 'Withdrawn' }
      ]
    }
  },
  computed: {
    ...mapState(['selectedOffice']),
    maxOfferAcceptedDate () {
      if (!this.completionForm) {
        return null
      }

      const today = moment().format('YYYY-MM-DD')

      return this.completionForm.exchanged_at && moment(this.completionForm.exchanged_at).isBefore(today) ? this.completionForm.exchanged_at : this.maxExchangeDate
    },
    maxExchangeDate () {
      if (!this.completionForm) {
        return null
      }

      const today = moment().format('YYYY-MM-DD')

      return this.completionForm.completed_at && moment(this.completionForm.completed_at).isBefore(today) ? this.completionForm.completed_at : today
    },
    maxCompletionDate () {
      if (!this.completionForm) {
        return null
      }

      // Completed. Max date is today
      if (this.completionForm.status_id === 2) {
        return moment().format('YYYY-MM-DD')
      }

      // Exchanged. Max date is 6 months from now.
      return moment().add(6, 'month').format('YYYY-MM-DD')
    },
    completionFormDisabled () {
      if (!this.completionForm || !this.fees) {
        return true
      }
      // Withdrawn option
      if (this.completionForm.status_id === 0 && this.completionForm.withdrawn_status_id === 1 && !this.completionForm.selling_agent) {
        return true
      }
      if (this.completionForm.status_id === 0 && !this.completionForm.withdrawn_status_id) {
        return true
      }

      if ([0, 3].includes(this.completionForm.status_id)) {
        return false
      }

      // Commission based fees
      if (this.fees.network.fee_basis_id === 2) {
        if (!this.completionForm.gross_commission || this.grossCommissionType === null) {
          return true
        }

        if (this.completionForm.has_third_party_commission && (this.netCommissionType === null || !this.completionForm.net_commission || !this.completionForm.third_party_name)) {
          return true
        }
      }

      // Shared fees
      return !this.completionForm.exchanged_at ||
        !this.completionForm.completed_at ||
        !this.completionForm.completion_price
    },
    commissionNetwork () {
      if (!this.completionForm) {
        return 0
      }
      const commission = this.completionForm.has_third_party_commission ? this.completionForm.net_commission : this.completionForm.gross_commission
      if (!commission) {
        return 0
      }

      return (commission / 100) * this.fees.office.commission_share
    },
    commissionOffice () {
      if (!this.completionForm) {
        return 0
      }
      const commission = this.completionForm.has_third_party_commission ? this.completionForm.net_commission : this.completionForm.gross_commission
      if (!commission) {
        return 0
      }

      return commission - this.commissionNetwork
    },
    commissionThirdParty () {
      if (!this.completionForm || !this.completionForm.has_third_party_commission) {
        return 0
      }

      return this.completionForm.gross_commission - this.completionForm.net_commission
    }
  },
  created () {
    this.search()
  },
  methods: {
    ...mapActions(['displayToastNotification']),
    search: debounce(function () {
      if (this.pageHasChanged) {
        this.pageHasChanged = false
      } else {
        this.searchParams.page = 1
      }

      v2Api.get('completion-report-properties/pending', {
        params: {
          ...this.searchParams,
          office_id: this.selectedOffice.id
        }
      }).then((response) => {
        this.listings = response.data.pending
        this.fees = response.data.fees
      }, (errors) => {
        this.error = errors.response.data.message
      })
    }, 500),
    save () {
      this.errorBag.clear()
      const url = this.completionForm.id ? `completion-report-properties/${this.completionForm.id}` : 'completion-report-properties'
      const method = this.completionForm.id ? 'patch' : 'post'

      let form = new FormData()
      for (let key in this.completionForm) {
        if (this.completionForm[key] === null) {
          continue
        }
        if (Array.isArray(this.completionForm[key])) {
          for (let i = 0; i < this.completionForm[key].length; i++) {
            form.append(`${key}[]`, this.completionForm[key][i])
          }
          continue
        }
        form.append(key, this.completionForm[key])
      }

      this.saving = true
      v2Api[method](url, form).then((response) => {
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Thank You',
          info: `The listing has been marked as ${this.completionStatus(response.data.status_id)}`
        })

        if (this.editListing) {
          this.listings.data.splice(this.listings.data.indexOf(this.editListing), 1)
        }

        this.completionForm = null
        this.editListing = null
        this.saving = false
        this.isConfirming = false
      }, (errors) => {
        this.errorBag.store(errors.response.data)

        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Validation Error',
          info: 'Please review your input and try again.'
        })

        this.saving = false
        this.isConfirming = false
      })
    },
    listingStatus (id) {
      switch (id) {
        case 2: return 'SSTC'
        case 3: return 'SSTCM'
        case 4: return 'Under Offer'
        case 5: return 'Reserved'
        case 6: return 'Let Agreed'
        case 7: return 'Sold'
        case 8: return 'Let'
        case 9: return 'Exchanged'
        case 10: return 'Completed'
        case 11: return 'Withdrawn'
        default: return 'Available'
      }
    },
    completionStatus (id) {
      switch (id) {
        case 0: return 'Withdrawn'
        case 1: return 'Exchanged'
        case 2: return 'Completed'
        case 3: return 'Duplicate Property Listing'
      }
    },
    closeModal () {
      this.editListing = null
      this.isConfirming = false
      this.completionForm = null
    },
    recalculateGrossCommission () {
      if (!this.completionForm || this.fees.network.fee_basis_id !== 2) {
        return
      }

      if (!this.completionForm.completion_price) {
        this.completionForm.gross_commission = 0
      } else {
        this.completionForm.gross_commission = (this.completionForm.completion_price / 100) * this.completionForm.gross_commission_percent
      }
      this.recalculateNetCommission()
    },
    recalculateNetCommission () {
      if (!this.completionForm || this.fees.network.fee_basis_id !== 2) {
        return
      }

      if (!this.completionForm.gross_commission) {
        this.completionForm.net_commission = 0
        return
      }

      this.completionForm.net_commission = (this.completionForm.gross_commission / 100) * this.completionForm.net_commission_percent
    },
    discloseNonListedCompletion () {
      this.completionForm = {
        office_id: this.selectedOffice.id,
        property_id: null,
        address: null,
        status_id: 2,
        final_offer_price: null,
        offer_accepted_at: null,
        exchanged_at: null,
        completed_at: null,
        completion_price: null,
        gross_commission: null,
        net_commission: null,
        has_third_party_commission: false,
        third_party_name: null,
        notes: null,
        gross_commission_percent: null,
        net_commission_percent: null
      }
    },
    pageChanged (i) {
      this.searchParams.page = i
      this.pageHasChanged = true
    },
    resetDates () {
      this.completionForm.completed_at = null
      this.completionForm.exchanged_at = null
    }
  },
  watch: {
    searchParams: {
      handler () {
        this.search()
      },
      deep: true
    },
    editListing () {
      if (!this.editListing) {
        this.completionForm = null
        return
      }

      this.errorBag.clear()
      const rpt = this.editListing.completion_report_property
      this.completionForm = rpt ? {
        id: rpt.id,
        status_id: rpt.status_id,
        final_offer_price: rpt.final_offer_price,
        offer_accepted_at: rpt.offer_accepted_at,
        exchanged_at: rpt.exchanged_at,
        completed_at: rpt.completed_at,
        completion_price: rpt.completion_price,
        gross_commission_percent: null,
        net_commission_percent: null,
        gross_commission: null,
        net_commission: null,
        has_third_party_commission: false,
        third_party_name: null,
        notes: null,
        attachments: []
      } : {
        office_id: this.editListing.office_id,
        property_id: this.editListing.id,
        status_id: null,
        final_offer_price: null,
        offer_accepted_at: null,
        exchanged_at: null,
        completed_at: null,
        completion_price: null,
        gross_commission_percent: null,
        net_commission_percent: null,
        gross_commission: null,
        net_commission: null,
        has_third_party_commission: false,
        third_party_name: null,
        notes: null,
        attachments: []
      }
    },
    selectedAddress: {
      handler (addr) {
        if (!addr.address_name_number) {
          return
        }

        this.completionForm.address = (addr ? [
          addr.address_name_number,
          addr.address_line2,
          addr.address_line3,
          addr.address_town,
          addr.address_line4,
          addr.address_postcode
        ].filter(Boolean).join(', ') : null) || null
      },
      deep: true
    },
    selectedOffice () {
      this.search()
    }
  }
}
</script>
